import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, LogOut, Download, Save, Ticket, Users, Video, Settings2, ListChecks, Lock } from "lucide-react";
import { Monogram, GoldRule } from "../components/ornaments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const inputCls =
  "w-full rounded-xl border border-gold/25 bg-navy/60 px-4 py-3 text-sm text-ivory placeholder:text-mist/50 outline-none transition-all duration-300 focus:border-gold/70";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      onLogin(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-5" data-testid="host-login">
      <form onSubmit={submit} className="glass-gold w-full max-w-sm rounded-3xl p-8 sm:p-10">
        <div className="flex justify-center"><Monogram size={52} /></div>
        <h1 className="mt-5 text-center font-serif text-3xl text-ivory">Host access</h1>
        <p className="mt-2 text-center text-xs uppercase tracking-[0.3em] text-mist">Private dashboard</p>
        <label htmlFor="host-email" className="mt-8 mb-2 block text-[10px] uppercase tracking-[0.3em] text-gold-light">Email</label>
        <input id="host-email" data-testid="host-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="host@celebration.local" className={inputCls} />
        <label htmlFor="host-password" className="mt-4 mb-2 block text-[10px] uppercase tracking-[0.3em] text-gold-light">Password</label>
        <input id="host-password" data-testid="host-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
        {error && <p className="mt-4 rounded-lg border border-red-400/40 bg-red-400/10 px-4 py-2.5 text-xs text-red-200" data-testid="host-login-error">{error}</p>}
        <button type="submit" data-testid="host-login-button" disabled={busy} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-gold-pale via-gold to-gold-deep py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-navy-deep transition-all duration-300 hover:shadow-[0_0_36px_rgba(212,175,55,0.35)] disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Lock size={14} />} Sign in
        </button>
      </form>
    </div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savedMsg, setSavedMsg] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [rsvpsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/host/rsvps`, { withCredentials: true }),
        axios.get(`${API}/host/settings`, { withCredentials: true }),
      ]);
      setData(rsvpsRes.data);
      setSettings(settingsRes.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    }
  };

  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSaving(true);
    setSavedMsg("");
    try {
      const { data: updated } = await axios.put(`${API}/host/settings`, settings, { withCredentials: true });
      setSettings(updated);
      setSavedMsg("Saved — new confirmations will use these details.");
      load();
    } catch (err) {
      setSavedMsg(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const downloadCsv = async () => {
    const { data: blob } = await axios.get(`${API}/host/rsvps.csv`, { withCredentials: true, responseType: "blob" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rsvps.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!data || !settings) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy">
        <Loader2 className="animate-spin text-gold" size={32} />
      </div>
    );
  }

  const stats = [
    { icon: Users, label: "Seats reserved", value: `${data.stats.seats_taken} / ${data.stats.capacity}` },
    { icon: Ticket, label: "Parties", value: data.stats.parties },
    { icon: Video, label: "Livestream requests", value: data.stats.stream_requests },
  ];

  return (
    <div className="min-h-screen bg-navy px-5 py-10 sm:px-8" data-testid="host-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Monogram size={44} />
            <div>
              <h1 className="font-serif text-2xl text-ivory sm:text-3xl">Guest list</h1>
              <p className="text-[10px] uppercase tracking-[0.3em] text-mist">Signed in as {user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="host-csv-button" onClick={downloadCsv} className="flex items-center gap-2 rounded-full border border-gold/40 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-light transition hover:bg-gold/10">
              <Download size={14} /> Download CSV
            </button>
            <button data-testid="host-logout-button" onClick={onLogout} className="flex items-center gap-2 rounded-full bg-gradient-to-r from-gold-pale via-gold to-gold-deep px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-navy-deep transition hover:shadow-[0_0_30px_rgba(212,175,55,0.35)]">
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {stats.map((s) => (
            <div key={s.label} className="glass-gold rounded-2xl p-5">
              <s.icon size={18} className="text-gold" />
              <p className="mt-3 font-serif text-3xl text-ivory" data-testid={`host-stat-${s.label.toLowerCase().replace(/ /g, "-")}`}>{s.value}</p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.25em] text-mist">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="glass-gold mt-8 overflow-hidden rounded-2xl">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm" data-testid="host-rsvp-table">
              <thead>
                <tr className="border-b border-gold/20 text-[10px] uppercase tracking-[0.2em] text-gold-light">
                  <th className="px-5 py-4 font-medium">Ticket</th>
                  <th className="px-5 py-4 font-medium">Name</th>
                  <th className="px-5 py-4 font-medium">Email</th>
                  <th className="px-5 py-4 font-medium">Party</th>
                  <th className="px-5 py-4 font-medium">Livestream</th>
                  <th className="px-5 py-4 font-medium">Note</th>
                </tr>
              </thead>
              <tbody className="font-light text-ivory/90">
                {data.rsvps.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-8 text-center text-mist">No RSVPs yet — they will appear here the moment a guest reserves a seat.</td></tr>
                )}
                {data.rsvps.map((r) => (
                  <tr key={r.ticket_code} className="border-b border-navy-card/60 last:border-0">
                    <td className="px-5 py-4 font-serif text-gold-light">{r.ticket_code}</td>
                    <td className="px-5 py-4">{r.name}</td>
                    <td className="px-5 py-4 text-mist">{r.email}</td>
                    <td className="px-5 py-4">{r.party_size}</td>
                    <td className="px-5 py-4">{r.wants_stream ? "Yes" : "—"}</td>
                    <td className="max-w-[220px] px-5 py-4 text-xs italic text-mist">{r.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="glass-gold mt-8 rounded-2xl p-6 sm:p-8" data-testid="host-settings">
          <h2 className="flex items-center gap-2 font-serif text-2xl text-ivory"><Settings2 size={18} className="text-gold" /> Email &amp; event settings</h2>
          <p className="mt-2 text-xs font-light text-mist">These details are included in every guest confirmation email.</p>
          <div className="mt-6 grid gap-5 lg:grid-cols-2">
            <div>
              <label htmlFor="set-address" className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-gold-light">Sarasota venue address (revealed by email)</label>
              <textarea id="set-address" data-testid="host-address-input" rows={3} value={settings.venue_address} onChange={(e) => setSettings({ ...settings, venue_address: e.target.value })} placeholder="e.g. 123 Bayfront Drive, Sarasota, FL" className={`${inputCls} resize-none`} />
            </div>
            <div className="space-y-5">
              <div>
                <label htmlFor="set-stream" className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-gold-light">Livestream link (December 15, 10:00 AM)</label>
                <input id="set-stream" data-testid="host-stream-input" value={settings.stream_link} onChange={(e) => setSettings({ ...settings, stream_link: e.target.value })} placeholder="https://… (private stream URL)" className={inputCls} />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="set-reply" className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-gold-light">Reply-to email</label>
                  <input id="set-reply" data-testid="host-reply-input" value={settings.reply_to} onChange={(e) => setSettings({ ...settings, reply_to: e.target.value })} placeholder="you@example.com" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="set-capacity" className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-gold-light">Seat capacity</label>
                  <input id="set-capacity" data-testid="host-capacity-input" type="number" min={1} max={500} value={settings.capacity} onChange={(e) => setSettings({ ...settings, capacity: parseInt(e.target.value, 10) || 1 })} className={inputCls} />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6 flex items-center gap-4">
            <button data-testid="host-settings-save" onClick={saveSettings} disabled={saving} className="flex items-center gap-2 rounded-full bg-gradient-to-r from-gold-pale via-gold to-gold-deep px-7 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-navy-deep transition-all duration-300 hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save settings
            </button>
            {savedMsg && <span className="text-xs text-gold-light" data-testid="host-settings-message">{savedMsg}</span>}
          </div>
        </div>

        <GoldRule className="mt-12 justify-center" />
        <p className="mt-6 pb-8 text-center text-[10px] uppercase tracking-[0.3em] text-mist/60">Graduation Celebration · Host Suite</p>
      </div>
    </div>
  );
};

export default function HostPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy">
        <Loader2 className="animate-spin text-gold" size={32} />
      </div>
    );
  }

  return user ? (
    <Dashboard user={user} onLogout={() => setUser(null)} />
  ) : (
    <Login onLogin={setUser} />
  );
}
